import {
  TRACK_SIGN_UP, TRACK_ADD_PROFILE,
  TRACK_EDIT_PROFILE, TRACK_SIGN_IN, TRACK_PLAY_VIDEO_CONTENT,
  TRACK_ADD_SUBSCRIPTION, TRACK_CHANGE_SUBSCRIPTION, TRACK_DELETE_PROFILE,
  TRACK_SIGN_OUT, TRACK_SEARCH_NO_RESULT, TRACK_SEARCH_RESULT,
  TRACK_MY_LIST, TRACK_SUBTITLE_CHANGED, TRACK_STOP_VIDEO_CONTENT,
  TRACK_CLICK_CONTENT_CARD, TRACK_DELETE_DEVICE, TRACK_DEVICE_LIMIT,
  TRACK_PURCHASE_INITIATE, TRACK_PURCHASE_COMPLETE,
  TRACK_ADD_VOUCHER, TRACK_PAUSE_VIDEO_CONTENT, TRACK_WINBACK_MODAL_CLICKED,
  TRACK_HOMEPAGE_LAND, TRACK_CANCEL_SUBSCRIPTION, TRACK_RESUME_SUBSCRIPTION,
  TRACK_CANCEL_JOURNEY, TRACK_ACTIVE_SESSION_STARTED, TRACK_ACTIVE_SESSION_ENDED,
  TRACK_BC_PLAYER_ERROR
} from './constants'
import { analytics } from './segment-setup'
import { hasSegmentUserID } from './segment-identify'
import { getCurrentProfile } from './getCurrentProfile'
import { getActiveSessionId, refreshSessionActivityWithTTLCheck } from './segment-session'

/**
 * Builds the standardized payload for all Segment track events.
 *
 * Responsibilities:
 * - Retrieves the current active session ID
 * - Refreshes the session activity timestamp if a session exists
 * - Attaches common fields shared across all events
 *
 * @param {object} eventDetails - Event-specific properties
 * @returns {object} Final payload merged with common fields
 */
const getSegmentEventPayload = (eventDetails = {}, { refreshSession = true } = {}) => {
  // Refresh the session TTL only for regular user events
  // Skip refreshing session TTL for signin/signup and session start/end events to prevent calling captureSessionEnd recursively
  const activeSessionId = refreshSession ? refreshSessionActivityWithTTLCheck() : getActiveSessionId()

  const commonFields = {
    userProfile: getCurrentProfile(),
    activeSessionId
  }

  return { ...commonFields, ...eventDetails }
}

/**
 * Provides functions to enable segment data collection
 * operations on the segment workspace "Sky-As-An-App".
 */
export const segmentTrackAddProfile = (eventDetails) => {
  analytics.track(TRACK_ADD_PROFILE, getSegmentEventPayload(eventDetails))
}

export const segmentTrackEditProfile = (eventDetails) => {
  analytics.track(TRACK_EDIT_PROFILE, getSegmentEventPayload(eventDetails))
}

export const segmentTrackDeleteProfile = (eventDetails) => {
  analytics.track(TRACK_DELETE_PROFILE, getSegmentEventPayload(eventDetails))
}

export const segmentTrackSignup = (eventDetails) => {
  analytics.track(TRACK_SIGN_UP, getSegmentEventPayload(eventDetails, { refreshSession: false }))
}

export const segmentTrackSignin = (eventDetails) => {
  analytics.track(TRACK_SIGN_IN, getSegmentEventPayload(eventDetails, { refreshSession: false }))
}

export const segmentTrackSignout = (eventDetails) => {
  analytics.track(TRACK_SIGN_OUT, getSegmentEventPayload(eventDetails))
  analytics.reset()
}

export const segmentTrackBCPlayerError = (eventDetails) => {
  analytics.track(TRACK_BC_PLAYER_ERROR, getSegmentEventPayload(eventDetails))
}

const getVideoContentTitle = ({
  title, series, seasonNumber, episodeNumber, typeName
}) => {
  if (typeName === 'Title') return title
  if (!series?.title || !seasonNumber || !episodeNumber) return title
  return `${series.title} S${seasonNumber} E${episodeNumber}`
}

const getVideoContentType = (typeName) => {
  if (typeName === 'Episode') return 'Show'
  if (typeName === 'Title') return 'Movie'
  return typeName
}

const getVideoContentPayload = (contentItem) => {
  const {
    title,
    ldId,
    __typename,
    rating,
    genres,
    episodeNumber,
    seasonNumber,
    series,
    isKids,
    isRental
  } = contentItem

  return {
    title: getVideoContentTitle({
      title, series, seasonNumber, episodeNumber, typeName: __typename
    }),
    contentId: ldId,
    type: getVideoContentType(__typename),
    rating: rating?.rating,
    genres: genres || series?.genres,
    episode: episodeNumber,
    season: seasonNumber,
    isKids: series ? series.isKids : isKids,
    isRental
  }
}

export const segmentTrackPlayVideo = (contentItem, ads, playbackDetails = {}) => {
  const containsPreRolls = ads?.some(ad => ad.type_ === 'pre')
  const containsMidRolls = ads?.some(ad => ad.type_ === 'mid')

  analytics.track(TRACK_PLAY_VIDEO_CONTENT, getSegmentEventPayload({
    ...getVideoContentPayload(contentItem),
    containsPreRolls,
    containsMidRolls,
    ...playbackDetails
  }))
}

export const segmentTrackStopVideo = (contentItem, eventDetails = {}) => {
  analytics.track(TRACK_STOP_VIDEO_CONTENT, getSegmentEventPayload({
    ...getVideoContentPayload(contentItem),
    ...eventDetails
  }))
}

export const segmentTrackPauseVideo = (contentItem, eventDetails = {}) => {
  analytics.track(TRACK_PAUSE_VIDEO_CONTENT, getSegmentEventPayload({
    contentId: contentItem.ldId,
    ...eventDetails
  }))
}

export const segmentTrackSubtitleChanged = (contentItem, eventDetails) => {
  const {
    title, type, contentId
  } = getVideoContentPayload(contentItem)
  analytics.track(TRACK_SUBTITLE_CHANGED, getSegmentEventPayload({
    ...eventDetails,
    title,
    type,
    contentId
  }))
}

export const segmentTrackAddSubscription = (eventDetails) => {
  analytics.track(TRACK_ADD_SUBSCRIPTION, getSegmentEventPayload(eventDetails))
}

export const segmentTrackChangeSubscription = (eventDetails) => {
  analytics.track(TRACK_CHANGE_SUBSCRIPTION, getSegmentEventPayload(eventDetails))
}

export const segmentTrackCancelSubscription = (eventDetails) => {
  analytics.track(TRACK_CANCEL_SUBSCRIPTION, getSegmentEventPayload(eventDetails))
}

export const segmentTrackResumeSubscription = (eventDetails) => {
  analytics.track(TRACK_RESUME_SUBSCRIPTION, getSegmentEventPayload(eventDetails))
}

export const segmentTrackSearchResult = (eventDetails) => {
  if (!hasSegmentUserID()) return
  analytics.track(TRACK_SEARCH_RESULT, getSegmentEventPayload(eventDetails))
}

export const segmentTrackSearchNoResult = (eventDetails) => {
  if (!hasSegmentUserID()) return
  analytics.track(TRACK_SEARCH_NO_RESULT, getSegmentEventPayload(eventDetails))
}

export const segmentTrackAddMyList = (contentItem) => {
  const {
    title, id, ldId, __typename, genres
  } = contentItem
  analytics.track(TRACK_MY_LIST, getSegmentEventPayload({
    title,
    ldId,
    type: __typename,
    contentId: id,
    genres
  }))
}

// My Lists and individual watchlist screens should be identified in card click events
const isWatchlistPath = path => path === '/my-lists' || path?.startsWith('/watchlist/')

export const segmentTrackClickContentCard = (contentItem, position, list, path, isInWatchlistOption = false) => {
  const {
    title, id, ldId, __typename, isRental
  } = contentItem
  if (!hasSegmentUserID()) return
  const isInWatchlist = isInWatchlistOption || isWatchlistPath(path)

  analytics.track(TRACK_CLICK_CONTENT_CARD, getSegmentEventPayload({
    title,
    contentId: id,
    ldId,
    isRental,
    type: __typename,
    indexInRail: position,
    railTitle: list,
    path,
    ...(isInWatchlist ? { isInWatchlist: true } : {})
  }))
}

export const segmentTrackDeleteDevice = (eventDetails) => {
  analytics.track(TRACK_DELETE_DEVICE, getSegmentEventPayload(eventDetails))
}

export const segmentTrackDeviceLimit = (eventDetails) => {
  analytics.track(TRACK_DEVICE_LIMIT, getSegmentEventPayload(eventDetails))
}

export const segmentTrackInitiatePurchase = (selectedPlan, detail) => {
  if (detail?.isRental) {
    const {
      title, isRental, ldId, products
    } = detail
    analytics.track(TRACK_PURCHASE_INITIATE, getSegmentEventPayload({
      title,
      isRental,
      ldId,
      payment: Number(products[0]?.currentPrice)
    }))
  } else {
    analytics.track(TRACK_PURCHASE_INITIATE, getSegmentEventPayload({
      planTitle: selectedPlan?.title,
      planSku: selectedPlan?.sku,
      paymentDue: selectedPlan?.paymentDue,
      payment: Number(selectedPlan?.payment),
      voucher: selectedPlan?.voucher,
      isRental: false
    }))
  }
}

export const segmentTrackCompletePurchase = (eventDetails) => {
  analytics.track(TRACK_PURCHASE_COMPLETE, getSegmentEventPayload({ ...eventDetails, isRental: eventDetails.isRental || false }))
}

export const segmentTrackAddVoucher = (eventDetails) => {
  analytics.track(TRACK_ADD_VOUCHER, getSegmentEventPayload(eventDetails))
}

export const segmentTrackClickOnWelcomeBackModal = (eventDetails) => {
  analytics.track(TRACK_WINBACK_MODAL_CLICKED, getSegmentEventPayload(eventDetails))
}

export const segmentTrackHomepageLand = (eventDetails) => {
  analytics.track(TRACK_HOMEPAGE_LAND, getSegmentEventPayload(eventDetails))
}

export const segmentTrackCancelJourney = (eventDetails) => {
  analytics.track(TRACK_CANCEL_JOURNEY, getSegmentEventPayload(eventDetails))
}

export const segmentTrackActiveSessionStarted = (eventDetails) => {
  analytics.track(TRACK_ACTIVE_SESSION_STARTED, getSegmentEventPayload(eventDetails, { refreshSession: false }))
}

export const segmentTrackActiveSessionEnded = (eventDetails) => {
  analytics.track(TRACK_ACTIVE_SESSION_ENDED, getSegmentEventPayload(eventDetails, { refreshSession: false }))
}
